import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, FolderKanban, FileText, Workflow, LibraryBig, ArrowRight, Layers3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface GestionDocumentaireModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'methodologie' | 'types' | 'valeur';

export default function GestionDocumentaireModal({ isOpen, onClose }: GestionDocumentaireModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');
  const navigate = useNavigate();

  if (!isOpen) return null;

  const tabs: { id: TabId; label: string }[] = [
    { id: 'vue', label: 'Vue d’ensemble' },
    { id: 'methodologie', label: 'Méthodologie 5 étapes' },
    { id: 'types', label: 'Types de documents' },
    { id: 'valeur', label: 'Valeur pour PRIsME' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-950 z-50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-slate-950 text-slate-100 border border-slate-700 shadow-2xl rounded-2xl w-[92vw] h-[92vh] flex flex-col overflow-hidden">
          <div className="border-b border-slate-800 px-6 py-5 bg-slate-950">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white text-slate-950 flex items-center justify-center shadow-lg">
                  <FolderKanban className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Gestion documentaire</h2>
                  <p className="text-sm text-slate-300">
                    Espace centralisé pour concevoir, structurer, normaliser et suivre les documents produits dans PRIsME avant leur mise en production.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900 p-2 text-slate-200 hover:bg-slate-800 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-6 pt-4 pb-3 border-b border-slate-800 bg-slate-950">
            <div className="flex flex-wrap gap-2 mb-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? 'bg-white text-slate-950'
                      : 'bg-slate-900 text-slate-300 border border-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => navigate('/gestion-documentaire')}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-4 py-3 transition"
            >
              Ouvrir la gestion documentaire
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 bg-slate-950 space-y-6">
            {activeTab === 'vue' && (
              <>
                <SectionHero
                  title="Vue d’ensemble"
                  subtitle="La gestion documentaire PRIsME est le centre de conception des documents normalisés utilisés dans les démarches d’accompagnement client. Elle permet de préparer les structures, les formulaires de prise de besoin, les squelettes de documents et les livrables avant leur utilisation officielle."
                  icon={<FileText className="w-5 h-5 text-cyan-300" />}
                />

                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[
                    'Gabarits : modèles et structures de base',
                    'Formulaires : prises de besoin structurées',
                    'Livrables : documents produits pour le client',
                    'Contenu en révision : documents à valider avant mise en production',
                    'Catalogue des actifs : données de base utilisées pour soutenir les analyses',
                    'Gestion documentaire : espace central pour organiser et piloter les documents',
                  ].map((item) => (
                    <InfoCard key={item} title={item} />
                  ))}
                </div>

                <HighlightBox title="Message clé">
                  Avant de produire un document client, PRIsME doit s’assurer que le contenu est structuré, cohérent, validé et aligné avec le cadre de référence BZ.
                </HighlightBox>
              </>
            )}

            {activeTab === 'methodologie' && (
              <>
                <SectionHero
                  title="Méthodologie PRIsME — Gestion documentaire"
                  subtitle="Processus structuré en 5 étapes pour créer des documents normés de haute qualité, combinant intelligence artificielle et expertise humaine."
                  icon={<Workflow className="w-5 h-5 text-cyan-300" />}
                />

                <section className="bg-slate-900 border border-slate-700 rounded-xl p-4">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                    <span className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2">1</span>
                    <span>→</span>
                    <span className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2">2</span>
                    <span>→</span>
                    <span className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2">3</span>
                    <span>→</span>
                    <span className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2">4</span>
                    <span>→</span>
                    <span className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2">5</span>
                  </div>
                </section>

                <div className="grid xl:grid-cols-2 gap-4">
                  <StepCard title="Étape 1 — Création des gabarits" description="Création, gouvernance et normalisation des modèles de documents. Cette étape permet de définir la structure de base, les sections obligatoires, le ton professionnel, les standards visuels et les règles de contenu." />
                  <StepCard title="Étape 2 — Remplir le formulaire" description="Complétion rigoureuse du formulaire de prise de besoin. Les informations recueillies servent à alimenter les documents, guider l’analyse et éviter les oublis importants." />
                  <StepCard title="Étape 3 — Génération du document" description="Production assistée par IA à partir des informations structurées. Le document généré respecte les standards PRIsME et les orientations définies dans le gabarit." />
                  <StepCard title="Étape 4 — Expertise humaine" description="Validation, ajustement, enrichissement et révision par l’expert. Cette étape est essentielle pour assurer la qualité, la pertinence, le jugement professionnel et l’adaptation au contexte client." />
                  <StepCard title="Étape 5 — Livraison" description="Livraison finale dans l’espace client ou dans l’environnement prévu. Le document devient alors un livrable officiel utilisable dans le cycle d’accompagnement." />
                </div>
              </>
            )}

            {activeTab === 'types' && (
              <>
                <SectionHero
                  title="Types de documents PRIsME"
                  subtitle="La gestion documentaire regroupe les principaux documents que PRIsME peut produire ou soutenir dans un accompagnement client."
                  icon={<LibraryBig className="w-5 h-5 text-cyan-300" />}
                />

                <div className="grid xl:grid-cols-2 gap-4">
                  <Card title="Documents stratégiques">
                    <BulletList items={[
                      'Plan directeur TI',
                      'Plan annuel TI',
                      'Plan stratégique TI 3 à 5 ans',
                      'Offre de service BZ',
                      'Document des besoins client',
                    ]} />
                  </Card>

                  <Card title="Documents cybersécurité">
                    <BulletList items={[
                      'Plan cybersécurité',
                      'ODS cybersécurité',
                      'Politique cybersécurité',
                      'Plan de reprise',
                      'Plan d’intervention',
                      'Tableau de sauvegarde',
                      'Analyse de risques',
                      'Plan d’action NIST / Loi 25',
                    ]} />
                  </Card>
                </div>
              </>
            )}

            {activeTab === 'valeur' && (
              <>
                <SectionHero
                  title="Valeur pour PRIsME"
                  subtitle="La gestion documentaire agit comme une assise structurante pour produire mieux, plus vite et avec davantage de cohérence à l’échelle de l’organisation."
                  icon={<Layers3 className="w-5 h-5 text-cyan-300" />}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <Card title="Objectif">
                    <p>
                      La gestion documentaire PRIsME permet de standardiser les livrables, formulaires, gabarits et documents stratégiques utilisés dans l’accompagnement client. Elle sert à assurer une qualité uniforme, une traçabilité claire et une meilleure cohérence entre les besoins clients, les livrables générés et les suivis réalisés.
                    </p>
                  </Card>

                  <Card title="Apport concret">
                    <BulletList items={[
                      'Normaliser les pratiques documentaires',
                      'Réduire les variations de qualité entre livrables',
                      'Accélérer la production grâce à des bases solides',
                      'Structurer l’utilisation combinée de l’IA et de l’expertise humaine',
                      'Assurer la cohérence entre besoins, analyses, décisions et suivis',
                    ]} />
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function SectionHero({ title, subtitle, icon }: { title: string; subtitle: string; icon: React.ReactNode }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>
      <p className="text-sm text-slate-300">{subtitle}</p>
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 space-y-3 shadow-lg">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <div className="text-sm text-slate-200 leading-relaxed">{children}</div>
    </section>
  );
}

function InfoCard({ title }: { title: string }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-lg">
      <p className="text-sm font-semibold text-white leading-relaxed">{title}</p>
    </section>
  );
}

function StepCard({ title, description }: { title: string; description: string }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 space-y-3 shadow-lg">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      <p className="text-sm text-slate-200 leading-relaxed">{description}</p>
    </section>
  );
}

function HighlightBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-lg">
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <div className="text-sm text-slate-200 leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2 text-sm text-slate-200">
          <span className="text-cyan-300 mt-0.5">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
